import React from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  Collapse,
  Link,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import type { AuditLog } from '@byte-store-control/shared';
import { ActionBadge } from './ActionBadge';
import { AuditDetailPanel } from './AuditDetailPanel';
import { formatRelativeTime } from '../../utils/formatTime';

interface AuditRowProps {
  log: AuditLog;
  isExpanded: boolean;
  onToggleExpand: () => void;
  showStoreColumn?: boolean;
  showUserColumn?: boolean;
  onStoreClick?: (storeNumber: string) => void;
}

export const AuditRow: React.FC<AuditRowProps> = ({
  log,
  isExpanded,
  onToggleExpand,
  showStoreColumn = true,
  showUserColumn = true,
  onStoreClick,
}) => {
  const hasDetails = Boolean(
    log.changes || log.affectedStores || log.entityId || log.ipAddress
  );

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: isExpanded ? 'none' : undefined },
          cursor: hasDetails ? 'pointer' : 'default',
        }}
        onClick={hasDetails ? onToggleExpand : undefined}
      >
        {/* Expand/Collapse Icon */}
        <TableCell padding="checkbox" sx={{ width: 48 }}>
          {hasDetails && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
            >
              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </TableCell>

        {/* Timestamp */}
        <TableCell>
          <Tooltip
            title={new Date(log.timestamp).toLocaleString('en-GB', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
            arrow
            placement="top"
          >
            <Typography variant="body2" sx={{ cursor: 'default' }}>
              {formatRelativeTime(log.timestamp)}
            </Typography>
          </Tooltip>
        </TableCell>

        {/* User */}
        {showUserColumn && (
          <TableCell>
            <Typography variant="body2" fontWeight={500}>
              {log.userName || 'Unknown User'}
            </Typography>
          </TableCell>
        )}

        {/* Action */}
        <TableCell>
          <ActionBadge action={log.action} />
        </TableCell>

        {/* Store */}
        {showStoreColumn && (
          <TableCell>
            {log.storeNumber === 'BULK' && log.affectedStores ? (
              // Bulk operation - show store number if single store, count if multiple
              log.affectedStores.length === 1 ? (
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStoreClick?.(log.affectedStores![0]);
                  }}
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {log.affectedStores[0]}
                </Link>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'primary.main',
                  }}
                >
                  {log.affectedStores.length} stores
                </Typography>
              )
            ) : log.storeNumber && log.storeNumber !== 'BULK' ? (
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation();
                  onStoreClick?.(log.storeNumber);
                }}
                sx={{
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {log.storeNumber}
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>

      {/* Expandable Detail Row */}
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={2 + (showStoreColumn ? 1 : 0) + (showUserColumn ? 1 : 0) + 1}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <AuditDetailPanel log={log} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
